import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { opsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Clock, Phone, UtensilsCrossed, Brain,
  Shield, Loader2, Save, Plus, Trash2, ChevronDown,
  Plug, Pencil, AlertTriangle, Sparkles, DollarSign, RefreshCw
} from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix"];

export default function OpsRestaurantPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("settings");

  // Settings
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [tz, setTz] = useState("");
  const [transferPhone, setTransferPhone] = useState("");
  const [greetingScript, setGreetingScript] = useState("");
  const [aiActive, setAiActive] = useState(false);
  const [hours, setHours] = useState({});

  // Menu
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [openCats, setOpenCats] = useState({});

  // Knowledge
  const [knowledge, setKnowledge] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  // Add Item Dialog
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemCatId, setAddItemCatId] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemDesc, setNewItemDesc] = useState("");

  // Edit Item Dialog
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Add Category Dialog
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  // Bulk delete confirm
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await opsAPI.getUser(userId);
      setData(res.data);
      const r = res.data.restaurant;
      if (r) {
        setBusinessName(r.business_name || ""); setAddress(r.address || "");
        setTz(r.timezone || ""); setTransferPhone(r.transfer_phone || "");
        setGreetingScript(r.greeting_script || ""); setAiActive(r.ai_active || false);
        setHours(r.hours || {});
      }
      const rid = res.data.restaurant?.id;
      if (rid) {
        const [catRes, itemRes, kRes] = await Promise.all([
          opsAPI.getRestaurantData(rid, "menu_categories"),
          opsAPI.getRestaurantData(rid, "menu_items"),
          opsAPI.getRestaurantData(rid, "knowledge"),
        ]);
        setCategories(catRes.data || []); setItems(itemRes.data || []); setKnowledge(kRes.data || []);
        const oc = {}; (catRes.data || []).forEach(c => { oc[c.id] = true; }); setOpenCats(oc);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rid = data?.restaurant?.id;

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await opsAPI.updateRestaurant(rid, { business_name: businessName, address, timezone: tz, transfer_phone: transferPhone, greeting_script: greetingScript, ai_active: aiActive, hours });
      toast.success("Settings saved");
    } catch (err) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleAddCategory = async () => {
    if (!newCatName) return;
    try {
      await opsAPI.createMenuCategory(rid, { name: newCatName, sort_order: categories.length });
      setAddCatOpen(false); setNewCatName("");
      fetchData();
      toast.success("Category created");
    } catch (err) { toast.error("Failed"); }
  };

  const handleAddItem = async () => {
    if (!newItemName || !newItemPrice || !addItemCatId) { toast.error("Fill all fields"); return; }
    try {
      await opsAPI.createMenuItem(rid, { category_id: addItemCatId, name: newItemName, price: parseFloat(newItemPrice), description: newItemDesc });
      setAddItemOpen(false); setNewItemName(""); setNewItemPrice(""); setNewItemDesc("");
      fetchData();
      toast.success("Item created");
    } catch (err) { toast.error("Failed"); }
  };

  const openEditItem = (item) => {
    setEditingItem({ ...item }); setEditItemOpen(true);
  };

  const handleSaveEditItem = async () => {
    if (!editingItem) return;
    try {
      await opsAPI.updateMenuItem(rid, editingItem.id, { name: editingItem.name, price: editingItem.price, description: editingItem.description, available: editingItem.available });
      setEditItemOpen(false); fetchData();
      toast.success("Item updated");
    } catch (err) { toast.error("Failed"); }
  };

  const handleDeleteItem = async (itemId) => {
    try { await opsAPI.deleteRestaurantMenuItem(rid, itemId); setItems(p => p.filter(i => i.id !== itemId)); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const handleDeleteKnowledge = async (entryId) => {
    try { await opsAPI.deleteRestaurantKnowledge(rid, entryId); setKnowledge(p => p.filter(k => k.id !== entryId)); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const handleBulkDelete = async () => {
    try {
      if (bulkDeleteType === "menu") { await opsAPI.deleteAllMenuItems(rid); setCategories([]); setItems([]); }
      else if (bulkDeleteType === "knowledge") { await opsAPI.deleteAllKnowledge(rid); setKnowledge([]); }
      setBulkDeleteOpen(false);
      toast.success(`All ${bulkDeleteType} entries deleted`);
    } catch (err) { toast.error("Failed"); }
  };

  const handleRegenerateKnowledge = async () => {
    setRegenerating(true);
    try {
      const res = await opsAPI.regenerateKnowledge(rid);
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error("Failed to regenerate"); }
    finally { setRegenerating(false); }
  };

  const updateDayHours = (day, field, value) => setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));

  if (loading) return <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!data?.restaurant) return <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center text-slate-500">No restaurant configured</div>;

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="ops-restaurant-page">
      <header className="sticky top-0 z-50 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/zest-logo.png" alt="Zest" className="h-8 w-auto" />
            <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px] ml-1"><Shield className="w-2.5 h-2.5 mr-1" /> OPS</Badge>
          </div>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs h-8" onClick={() => navigate(`/ops/users/${userId}`)}>
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to User
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center"><Building2 className="w-6 h-6 text-orange-500" /></div>
          <div>
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.restaurant.business_name}</h1>
            <p className="text-sm text-slate-500">{data.restaurant.address} | {data.user?.name} ({data.user?.email})</p>
          </div>
          <div className="flex gap-2 ml-auto">
            <Badge className={data.restaurant.ai_active ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-red-50 text-red-500 border-red-200"} variant="outline">AI {data.restaurant.ai_active ? "Active" : "Inactive"}</Badge>
            <Badge className={data.restaurant.pos_connected ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-slate-100 text-slate-400"} variant="outline"><Plug className="w-3 h-3 mr-1" /> {data.restaurant.pos_connected ? data.restaurant.pos_type : "No POS"}</Badge>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200">
          {[
            { id: "settings", label: "Settings", icon: Building2 },
            { id: "menu", label: `Menu (${items.length})`, icon: UtensilsCrossed },
            { id: "knowledge", label: `Knowledge (${knowledge.length})`, icon: Brain },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`ops-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* ─── Settings Tab ─── */}
        {tab === "settings" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="border-slate-100">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Business Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5"><Label className="text-xs">Business Name</Label><Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="h-9" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="h-9" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Timezone</Label>
                  <Select value={tz} onValueChange={setTz}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t.replace("America/","")}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Transfer Phone</Label><Input value={transferPhone} onChange={(e) => setTransferPhone(e.target.value)} className="h-9" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Twilio Number</Label><p className="text-sm font-mono text-slate-600">{data.restaurant.twilio_number || "Not provisioned"}</p></div>
                <div className="flex items-center justify-between"><Label className="text-xs">AI Active</Label><Switch checked={aiActive} onCheckedChange={setAiActive} /></div>
              </CardContent>
            </Card>
            <div className="space-y-5">
              <Card className="border-slate-100">
                <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Greeting Script</CardTitle></CardHeader>
                <CardContent><Textarea value={greetingScript} onChange={(e) => setGreetingScript(e.target.value)} rows={3} className="text-sm" /></CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Hours of Operation</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {DAYS.map(day => (
                    <div key={day} className="flex items-center gap-3 py-1">
                      <span className="w-16 text-xs font-medium capitalize text-slate-600">{day.slice(0,3)}</span>
                      <Switch checked={!hours[day]?.closed} onCheckedChange={(v) => updateDayHours(day, "closed", !v)} />
                      {!hours[day]?.closed && (<><Input type="time" value={hours[day]?.open || ""} onChange={(e) => updateDayHours(day, "open", e.target.value)} className="w-24 h-7 text-xs" /><span className="text-xs text-slate-400">to</span><Input type="time" value={hours[day]?.close || ""} onChange={(e) => updateDayHours(day, "close", e.target.value)} className="w-24 h-7 text-xs" /></>)}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            <div className="lg:col-span-2"><Button onClick={handleSaveSettings} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Settings</Button></div>
          </div>
        )}

        {/* ─── Menu Tab ─── */}
        {tab === "menu" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" className="h-8 text-xs" onClick={() => setAddCatOpen(true)} data-testid="ops-add-category"><Plus className="w-3 h-3 mr-1" /> Add Category</Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setAddItemCatId(categories[0]?.id || ""); setAddItemOpen(true); }} data-testid="ops-add-item"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
              <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setBulkDeleteType("menu"); setBulkDeleteOpen(true); }} data-testid="ops-delete-all-menu"><Trash2 className="w-3 h-3 mr-1" /> Delete All</Button>
            </div>
            {categories.length === 0 ? (
              <Card className="border-slate-100 border-dashed"><CardContent className="py-12 text-center text-slate-400">No menu items. Add categories and items above.</CardContent></Card>
            ) : categories.map(cat => {
              const catItems = items.filter(i => i.category_id === cat.id);
              return (
                <Collapsible key={cat.id} open={openCats[cat.id]} onOpenChange={(o) => setOpenCats(p => ({...p, [cat.id]: o}))}>
                  <Card className="border-slate-100">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-slate-50/50 py-3 px-5 flex flex-row items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{cat.name}</CardTitle>
                          <Badge variant="secondary" className="text-[10px]">{catItems.length}</Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400" onClick={(e) => { e.stopPropagation(); setAddItemCatId(cat.id); setAddItemOpen(true); }}><Plus className="w-3 h-3" /></Button>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openCats[cat.id] ? "rotate-180" : ""}`} />
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-4 px-5 space-y-1.5">
                        {catItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50/50">
                            <div className="flex-1 min-w-0">
                              <span className="text-[13px] font-medium text-slate-800">{item.name}</span>
                              <span className="text-[13px] text-slate-500 ml-2">${item.price?.toFixed(2)}</span>
                              {item.description && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{item.description}</p>}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Badge variant={item.available ? "default" : "secondary"} className={item.available ? "bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" : "text-[9px]"}>{item.available ? "Avail" : "Off"}</Badge>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-blue-600" onClick={() => openEditItem(item)}><Pencil className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDeleteItem(item.id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })}
          </div>
        )}

        {/* ─── Knowledge Tab ─── */}
        {tab === "knowledge" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" className="h-8 text-xs" onClick={handleRegenerateKnowledge} disabled={regenerating} data-testid="ops-regen-knowledge">
                {regenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1 text-amber-500" />} AI Regenerate
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setBulkDeleteType("knowledge"); setBulkDeleteOpen(true); }} data-testid="ops-delete-all-knowledge"><Trash2 className="w-3 h-3 mr-1" /> Delete All</Button>
            </div>
            {knowledge.length === 0 ? (
              <Card className="border-slate-100 border-dashed"><CardContent className="py-12 text-center text-slate-400">No knowledge entries. Click AI Regenerate above.</CardContent></Card>
            ) : knowledge.map(entry => (
              <Card key={entry.id} className="border-slate-100">
                <CardContent className="p-4 flex items-start gap-3">
                  <Brain className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {entry.question && <p className="text-[13px] font-semibold text-slate-800">{entry.question}</p>}
                    <p className="text-[13px] text-slate-600 leading-relaxed">{entry.answer}</p>
                    <div className="flex gap-1 mt-1.5">
                      <Badge variant="outline" className="text-[9px] text-slate-400">{entry.category}</Badge>
                      {entry.tags?.map((t, i) => <Badge key={i} variant="secondary" className="text-[9px]">{t}</Badge>)}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500 shrink-0" onClick={() => handleDeleteKnowledge(entry.id)}><Trash2 className="w-3 h-3" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent><DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Menu Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-sm">Category</Label>
              <Select value={addItemCatId} onValueChange={setAddItemCatId}><SelectTrigger className="h-9"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-sm">Name *</Label><Input value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Pepperoni Pizza" /></div>
            <div className="space-y-1.5"><Label className="text-sm">Price *</Label>
              <div className="relative"><DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input type="number" step="0.01" value={newItemPrice} onChange={(e) => setNewItemPrice(e.target.value)} placeholder="14.99" className="pl-8" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm">Description</Label><Input value={newItemDesc} onChange={(e) => setNewItemDesc(e.target.value)} placeholder="Classic pepperoni with mozzarella" /></div>
          </div>
          <DialogFooter><Button variant="secondary" onClick={() => setAddItemOpen(false)}>Cancel</Button><Button onClick={handleAddItem}>Add Item</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editItemOpen} onOpenChange={setEditItemOpen}>
        <DialogContent><DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Item</DialogTitle></DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-sm">Name</Label><Input value={editingItem.name} onChange={(e) => setEditingItem(p => ({...p, name: e.target.value}))} /></div>
              <div className="space-y-1.5"><Label className="text-sm">Price</Label>
                <div className="relative"><DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input type="number" step="0.01" value={editingItem.price} onChange={(e) => setEditingItem(p => ({...p, price: parseFloat(e.target.value) || 0}))} className="pl-8" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-sm">Description</Label><Input value={editingItem.description || ""} onChange={(e) => setEditingItem(p => ({...p, description: e.target.value}))} /></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Available</Label><Switch checked={editingItem.available} onCheckedChange={(v) => setEditingItem(p => ({...p, available: v}))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="secondary" onClick={() => setEditItemOpen(false)}>Cancel</Button><Button onClick={handleSaveEditItem}>Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={addCatOpen} onOpenChange={setAddCatOpen}>
        <DialogContent><DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Category</DialogTitle></DialogHeader>
          <div className="space-y-4"><div className="space-y-1.5"><Label className="text-sm">Category Name</Label><Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Salads" /></div></div>
          <DialogFooter><Button variant="secondary" onClick={() => setAddCatOpen(false)}>Cancel</Button><Button onClick={handleAddCategory}>Add Category</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirm */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent><DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}><AlertTriangle className="w-5 h-5" /> Delete All {bulkDeleteType === "menu" ? "Menu Items" : "Knowledge Entries"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will permanently delete all {bulkDeleteType === "menu" ? "menu items and categories" : "knowledge entries"} for this restaurant. This cannot be undone.</p>
          <DialogFooter><Button variant="secondary" onClick={() => setBulkDeleteOpen(false)}>Cancel</Button><Button variant="destructive" onClick={handleBulkDelete}>Delete All</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
